import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import { Table, TableHead, TableBody, TableCell, TableContainer, TableRow, TablePagination, Collapse, Select, MenuItem } from '@mui/material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { dataService } from '../../../Services/data.service'
import { recyclingHistoryTableColumns, recyclingMachine } from '../../../Config/constants';
import FlagstoneTheme from '../../../Config/flagstoneTheme';
import { connect } from 'react-redux';
import { ArrowUp, ArrowDown, Search, ChevronDown, ChevronUp } from 'lucide-react';

export const RecyclingHistoryOverviewTable = (props) => {
    const classes = useStyles();

    const { t } = useTranslation();
    const [pg, setpg] = useState(0);
    const [rpg, setrpg] = useState(25);
    const [filteredRecyclingHistory, setFilteredRecyclingHistory] = useState([]);
    const [localRecyclingHistory, setLocalRecyclingHistory] = useState([]);
    const [filterValues, setFilterValues] = useState([
        { type: recyclingHistoryTableColumns.ID, value: "", groupingUp: false, groupingDown: false },
        { type: recyclingHistoryTableColumns.DATE, value: "", groupingUp: false, groupingDown: false },
        { type: recyclingHistoryTableColumns.BARCODE, value: "", groupingUp: false, groupingDown: false },
        { type: recyclingHistoryTableColumns.OPERATOR, value: "", groupingUp: false, groupingDown: false },
        { type: recyclingHistoryTableColumns.BOOKINGTYPE, value: "", groupingUp: false, groupingDown: false },
        { type: recyclingHistoryTableColumns.CARRIERTYPE, value: "", groupingUp: false, groupingDown: false },
        { type: recyclingHistoryTableColumns.MATERIALTYPE, value: "", groupingUp: false, groupingDown: false },
        { type: recyclingHistoryTableColumns.MATERIALCOLOR, value: "", groupingUp: false, groupingDown: false },
        { type: recyclingHistoryTableColumns.NETWEIGHT, value: "", groupingUp: false, groupingDown: false },
        { type: recyclingHistoryTableColumns.WORKSTATION, value: "", groupingUp: false, groupingDown: false },
        { type: recyclingHistoryTableColumns.WASTELOCATION, value: "", groupingUp: false, groupingDown: false }
    ]);
    const [recyclingWorkstations, setRecyclingWorkstations] = useState([]);
    const [wasteLocations, setWasteLocations] = useState([])
    const [scrapCarrierTypes, setScrapCarrierTypes] = useState([]);
    const [scrapMaterialTypes, setScrapMaterialTypes] = useState([]);
    const [scrapColors, setScrapColors] = useState([]);

    const bookingTypes = [
        { id: 1, name: "Register" },
        { id: 2, name: "Write off" }
    ];

    useEffect(() => {
        dataService.getMachinesByMachineType(recyclingMachine).then(stations => {
            // order alphabetically
            setRecyclingWorkstations(stations.sort((a, b) => a.name.localeCompare(b.name)));
        }).catch((error) => { toast.error(error, { position: "top-center" }) });

        dataService.getScrapCarrierTypes().then((response) => {
            setScrapCarrierTypes(response.sort((a, b) => t(a.name).localeCompare(t(b.name))));
        }).catch((error) => {
            console.log(error);
        });

        dataService.getScrapMaterialTypes().then((response) => {
            setScrapMaterialTypes(response.sort((a, b) => t(a.name).localeCompare(t(b.name))));
        }).catch((error) => {
            console.log(error);
        })

        dataService.getWasteLocations().then((response) => {
            setWasteLocations(response.sort((a, b) => t(a.name).localeCompare(t(b.name))));
        }).catch((error) => {
            console.log(error);
        })

        dataService.getScrapColors().then((response) => {
            setScrapColors(response.sort((a, b) => t(a.colorName).localeCompare(t(b.colorName))));
        }).catch((error) => {
            console.log(error);
        });
    }, []);

    useEffect(() => {
        let tempRecyclingHistory = props.recyclingHistory.map(x => ({
            ...x,
            visible: true
        }));
        setLocalRecyclingHistory(tempRecyclingHistory);
        setFilteredRecyclingHistory(tempRecyclingHistory);
    }, [props.recyclingHistory]);

    useEffect(() => {
        if (!props.showFilters) {
            handleRemoveFilters();
        }
    }, [props.showFilters]);

    function handleChangePage(event, newpage) {
        setpg(newpage);
    }

    function handleChangeRowsPerPage(event) {
        setrpg(parseInt(event.target.value));
        setpg(0);
    }

    const formatDate = (date) => {
        let d = new Date(date);

        let optionsDate = { day: '2-digit', month: '2-digit', year: 'numeric' };
        let optionsTime = { hour: '2-digit', minute: '2-digit' };

        let dateString = d.toLocaleDateString('en-GB', optionsDate).replace(/\//g, '-');
        let timeString = d.toLocaleTimeString('en-GB', optionsTime);

        return dateString + " (" + timeString + ")";
    }

    const formatId = (id) => {
        return id.toString().padStart(6, '0');
    }

    const formatBookingType = (bookingType) => {
        switch (bookingType) {
            case "Register":
                return (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "4px" }}>
                        <p style={{ color: "#5CC958", margin: "0" }}>{t(bookingType)}</p>
                        <ArrowUp size={24} style={{ color: "#5CC958" }} />
                    </div>
                );
            case "Write off":
                return (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "4px" }}>
                        <p style={{ color: "#D84949", margin: "0" }}>{t(bookingType)}</p>
                        <ArrowDown size={24} style={{ color: "#D84949" }} />
                    </div>
                );
            default:
                return "-";
        }
    }

    const formatColor = (materialColor, materialColorHex) => {
        return (
            <div className={classes.colorContainer}>
                <div className={classes.colorSwatch} style={{ backgroundColor: materialColorHex, border: materialColorHex != "#000000" ? "1px solid #989898" : "none" }}></div>
                <p className={classes.colorName}>{materialColor}</p>
            </div>
        );
    }

    const formatWeight = (weight) => {
        // return weight as 999 999.999 kg
        let weightString = weight.toFixed(3);
        let weightParts = weightString.split(".");
        let weightKg = weightParts[0];
        let weightGrams = weightParts[1];

        return weightKg.replace(/\B(?=(\d{3})+(?!\d))/g, " ") + "," + weightGrams + " kg";
    }

    const sortWasteLocations = (wasteLocations) => {
        return wasteLocations.sort((a, b) => {
            // Check if either id is 0, and prioritize it
            if (a.id === 0) return -1;
            if (b.id === 0) return 1;
            // Otherwise, sort alphabetically by name
            return a.name.localeCompare(b.name);
        });
    };

    const handleRemoveFilters = () => {
        let tempRecyclingHistory = props.recyclingHistory.map(x => ({
            ...x,
            visible: true
        }));

        setFilterValues([
            { type: recyclingHistoryTableColumns.ID, value: "", groupingUp: false, groupingDown: false },
            { type: recyclingHistoryTableColumns.DATE, value: "", groupingUp: false, groupingDown: false },
            { type: recyclingHistoryTableColumns.BARCODE, value: "", groupingUp: false, groupingDown: false },
            { type: recyclingHistoryTableColumns.OPERATOR, value: "", groupingUp: false, groupingDown: false },
            { type: recyclingHistoryTableColumns.BOOKINGTYPE, value: "", groupingUp: false, groupingDown: false },
            { type: recyclingHistoryTableColumns.CARRIERTYPE, value: "", groupingUp: false, groupingDown: false },
            { type: recyclingHistoryTableColumns.MATERIALTYPE, value: "", groupingUp: false, groupingDown: false },
            { type: recyclingHistoryTableColumns.MATERIALCOLOR, value: "", groupingUp: false, groupingDown: false },
            { type: recyclingHistoryTableColumns.NETWEIGHT, value: "", groupingUp: false, groupingDown: false },
            { type: recyclingHistoryTableColumns.WORKSTATION, value: "", groupingUp: false, groupingDown: false },
            { type: recyclingHistoryTableColumns.WASTELOCATION, value: "", groupingUp: false, groupingDown: false }
        ])

        setFilteredRecyclingHistory(tempRecyclingHistory);
    }

    const handleSearch = (e, column) => {
        let value;

        value = e.target.value

        let tempFilterValues = [...filterValues];

        if (tempFilterValues.some(x => x.type === column)) {
            tempFilterValues.find(x => x.type === column).value = value;
        } else {
            tempFilterValues.push({ type: column, value: value });
        }

        let filterPresent = false;

        tempFilterValues.map(filterValue => {
            if (filterValue.value !== null && (filterValue.value.length > 0 || filterValue.value == true || filterValue.groupingUp == true || filterValue.groupingDown == true)) {
                filterPresent = true;
            }
        })

        if (!filterPresent) {
            tempFilterValues = [
                { type: recyclingHistoryTableColumns.ID, value: "", groupingUp: false, groupingDown: false },
                { type: recyclingHistoryTableColumns.DATE, value: "", groupingUp: false, groupingDown: false },
                { type: recyclingHistoryTableColumns.BARCODE, value: "", groupingUp: false, groupingDown: false },
                { type: recyclingHistoryTableColumns.OPERATOR, value: "", groupingUp: false, groupingDown: false },
                { type: recyclingHistoryTableColumns.BOOKINGTYPE, value: "", groupingUp: false, groupingDown: false },
                { type: recyclingHistoryTableColumns.CARRIERTYPE, value: "", groupingUp: false, groupingDown: false },
                { type: recyclingHistoryTableColumns.MATERIALTYPE, value: "", groupingUp: false, groupingDown: false },
                { type: recyclingHistoryTableColumns.MATERIALCOLOR, value: "", groupingUp: false, groupingDown: false },
                { type: recyclingHistoryTableColumns.NETWEIGHT, value: "", groupingUp: false, groupingDown: false },
                { type: recyclingHistoryTableColumns.WORKSTATION, value: "", groupingUp: false, groupingDown: false },
                { type: recyclingHistoryTableColumns.WASTELOCATION, value: "", groupingUp: false, groupingDown: false }
            ]
        }

        filterAndGroupRecyclingHistory(tempFilterValues);
        setFilterValues(tempFilterValues);
    }

    const getGroupingIconColor = (type, up) => {
        if (filterValues.some(x => x.type === type)) {
            if (up) {
                if (filterValues.find(x => x.type === type).groupingUp) {
                    return "#4654FB";
                }
            }
            else {
                if (filterValues.find(x => x.type === type).groupingDown) {
                    return "#4654FB";
                }
            }
        }
        return "rgba(12, 14, 41, .6)";
    }

    const getGroupingIconSize = (type, up) => {
        if (filterValues.some(x => x.type === type)) {
            if (up) {
                if (filterValues.find(x => x.type === type).groupingUp) {
                    return 16;
                }
            }
            else {
                if (filterValues.find(x => x.type === type).groupingDown) {
                    return 16;
                }
            }
        }
        return 12;
    }

    const handleGrouping = (column, up) => {
        let tempFilterValues = [...filterValues];

        if (tempFilterValues.some(x => x.type === column)) {
            if (up) {
                // Check if already grouped up
                if (tempFilterValues.find(x => x.type === column).groupingUp) {
                    // Remove grouping if already grouped up
                    tempFilterValues.find(x => x.type === column).groupingUp = false;
                } else {
                    // Group up
                    tempFilterValues.find(x => x.type === column).groupingUp = true;
                    tempFilterValues.find(x => x.type === column).groupingDown = false;
                }
            } else {
                // Check if already grouped up
                if (tempFilterValues.find(x => x.type === column).groupingDown) {
                    // Remove grouping if already grouped down
                    tempFilterValues.find(x => x.type === column).groupingDown = false;
                } else {
                    // Group down
                    tempFilterValues.find(x => x.type === column).groupingDown = true;
                    tempFilterValues.find(x => x.type === column).groupingUp = false
                }
            }
        } else {
            tempFilterValues.push({ type: column, value: "", groupingUp: up, groupingDown: !up });
        }

        filterAndGroupRecyclingHistory(tempFilterValues);
        setFilterValues(tempFilterValues);
    }

    const filterAndGroupRecyclingHistory = (filterValues) => {
        let tempRecyclingHistory = JSON.parse(JSON.stringify(localRecyclingHistory))

        filterValues.forEach(filterValue => {
            var value = filterValues.find(x => x.type === filterValue.type).value;
            switch (filterValue.type) {
                case recyclingHistoryTableColumns.DATE:
                    if (value !== null && value !== "") {
                        let filterDate = new Date(value);

                        tempRecyclingHistory.map(x => {
                            if (x.date) {
                                let date = new Date(x.date);
                                if (date.toDateString() !== filterDate.toDateString()) {
                                    x.visible = false;
                                }
                            } else {
                                x.visible = false;
                            }
                        });
                    }
                    if (filterValue.groupingUp) {
                        tempRecyclingHistory.sort((a, b) => (a.date > b.date) ? 1 : -1);
                    }
                    if (filterValue.groupingDown) {
                        tempRecyclingHistory.sort((a, b) => (a.date < b.date) ? 1 : -1);
                    }
                    break;
                case recyclingHistoryTableColumns.ID:
                    if (value !== null && value !== "") {
                        tempRecyclingHistory.map(x => { if (x.id) { if (!formatId(x.id.toString().toLowerCase()).includes(value.toLowerCase())) { x.visible = false } } else { x.visible = false } })
                    }
                    if (filterValue.groupingUp) {
                        tempRecyclingHistory.sort((a, b) => (a.id > b.id) ? 1 : -1);
                    }
                    if (filterValue.groupingDown) {
                        tempRecyclingHistory.sort((a, b) => (a.id < b.id) ? 1 : -1);
                    }
                    break;
                case recyclingHistoryTableColumns.BARCODE:
                    if (value !== null && value !== "") {
                        tempRecyclingHistory.map(x => { if (x.barcode) { if (!x.barcode.toString().toLowerCase().includes(value.toString().toLowerCase())) { x.visible = false; } } else { x.visible = false } });
                    }
                    if (filterValue.groupingUp) {
                        tempRecyclingHistory.sort((a, b) => (a.barcode > b.barcode) ? 1 : -1);
                    }
                    if (filterValue.groupingDown) {
                        tempRecyclingHistory.sort((a, b) => (a.barcode < b.barcode) ? 1 : -1);
                    }
                    break;
                case recyclingHistoryTableColumns.OPERATOR:
                    if (value !== null && value !== "") {
                        tempRecyclingHistory.map(x => { if (x.operator) { if (!x.operator.toString().toLowerCase().includes(value.toString().toLowerCase())) { x.visible = false; } } else { x.visible = false } });
                    }
                    if (filterValue.groupingUp) {
                        tempRecyclingHistory.sort((a, b) => (a.operator > b.operator) ? 1 : -1);
                    }
                    if (filterValue.groupingDown) {
                        tempRecyclingHistory.sort((a, b) => (a.operator < b.operator) ? 1 : -1);
                    }
                    break;
                case recyclingHistoryTableColumns.BOOKINGTYPE:
                    if (value !== null && value !== "") {
                        tempRecyclingHistory.map(x => { if (x.bookingType) { if (!x.bookingType.toString().toLowerCase().includes(value.toString().toLowerCase())) { x.visible = false; } } else { x.visible = false } });
                    }
                    if (filterValue.groupingUp) {
                        tempRecyclingHistory.sort((a, b) => (a.bookingType > b.bookingType) ? 1 : -1);
                    }
                    if (filterValue.groupingDown) {
                        tempRecyclingHistory.sort((a, b) => (a.bookingType < b.bookingType) ? 1 : -1);
                    }
                    break;
                case recyclingHistoryTableColumns.CARRIERTYPE:
                    if (value !== null && value !== "") {
                        tempRecyclingHistory.map(x => { if (x.carrierType) { if (!x.carrierType.toString().toLowerCase().includes(value.toLowerCase())) { x.visible = false; } } else { x.visible = false } });
                    }
                    if (filterValue.groupingUp) {
                        tempRecyclingHistory.sort((a, b) => (a.carrierType > b.carrierType) ? 1 : -1);
                    }
                    if (filterValue.groupingDown) {
                        tempRecyclingHistory.sort((a, b) => (a.carrierType < b.carrierType) ? 1 : -1);
                    }
                    break;
                case recyclingHistoryTableColumns.MATERIALTYPE:
                    if (value !== null && value !== "") {
                        tempRecyclingHistory.map(x => {
                            if (x.materialType) { if (!x.materialType.toString().toLowerCase().includes(value.toString().toLowerCase())) { x.visible = false; } } else { x.visible = false }
                        });
                    }
                    if (filterValue.groupingUp) {
                        tempRecyclingHistory.sort((a, b) => (a.materialType > b.materialType) ? 1 : -1);
                    }
                    if (filterValue.groupingDown) {
                        tempRecyclingHistory.sort((a, b) => (a.materialType < b.materialType) ? 1 : -1);
                    }
                    break;
                case recyclingHistoryTableColumns.MATERIALCOLOR:
                    if (value !== null && value !== "") {
                        tempRecyclingHistory.map(x => { if (x.materialColor) { if (!x.materialColor.toString().toLowerCase().includes(value.toLowerCase())) { x.visible = false; } } else { x.visible = false } });
                    }
                    if (filterValue.groupingUp) {
                        tempRecyclingHistory.sort((a, b) => (a.materialColor > b.materialColor) ? 1 : -1);
                    }
                    if (filterValue.groupingDown) {
                        tempRecyclingHistory.sort((a, b) => (a.materialColor < b.materialColor) ? 1 : -1);
                    }
                    break;
                case recyclingHistoryTableColumns.NETWEIGHT:
                    if (value !== null && value !== "") {
                        tempRecyclingHistory.map(x => { if (x.netWeight) { if (!x.netWeight.toString().toLowerCase().includes(value.toLowerCase())) { x.visible = false; } } else { x.visible = false } });
                    }
                    if (filterValue.groupingUp) {
                        tempRecyclingHistory.sort((a, b) => (a.netWeight > b.netWeight) ? 1 : -1);
                    }
                    if (filterValue.groupingDown) {
                        tempRecyclingHistory.sort((a, b) => (a.netWeight < b.netWeight) ? 1 : -1);
                    }
                    break;
                case recyclingHistoryTableColumns.WASTELOCATION:
                    if (value !== null && value !== "") {
                        tempRecyclingHistory.map(x => { if (x.wasteLocation) { if (!x.wasteLocation.toString().toLowerCase().includes(value.toLowerCase())) { x.visible = false; } } else { x.visible = false } });
                    }
                    if (filterValue.groupingUp) {
                        tempRecyclingHistory.sort((a, b) => (a.wasteLocation > b.wasteLocation) ? 1 : -1);
                    }
                    if (filterValue.groupingDown) {
                        tempRecyclingHistory.sort((a, b) => (a.wasteLocation < b.wasteLocation) ? 1 : -1);
                    }
                    break;
                case recyclingHistoryTableColumns.WORKSTATION:
                    if (value !== null && value !== "") {
                        tempRecyclingHistory.map(x => { if (x.workstation) { if (!x.workstation.toString().toLowerCase().includes(value.toLowerCase())) { x.visible = false; } } else { x.visible = false } });
                    }
                    if (filterValue.groupingUp) {
                        tempRecyclingHistory.sort((a, b) => (a.workstation > b.workstation) ? 1 : -1);
                    }
                    if (filterValue.groupingDown) {
                        tempRecyclingHistory.sort((a, b) => (a.workstation < b.workstation) ? 1 : -1);
                    }
                    break;
                default:
                    break;
            }
        });
        setFilteredRecyclingHistory(tempRecyclingHistory);
    }

    return (
        <React.Fragment>
            {props.loading ?
                <div className={classes.loaderContainer}>
                    <span className={classes.loader} />
                </div> :
                <div className={classes.tableContainer}>
                    <React.Fragment>
                        <TableContainer className={classes.tableContainer}>
                            <Table className={classes.table} aria-label="customized table">
                                <TableHead className={classes.head}>
                                    {/* Filters */}
                                    <TableRow className={classes.head__filterRow}>
                                        <TableCell className={classes.head__filterCell}>
                                            <Collapse in={props.showFilters} timeout={200} className={classes.head__collapse}>
                                                <input className={classes.head__filterInput_date} type="date" value={filterValues[recyclingHistoryTableColumns.DATE].value} onChange={(e) => handleSearch(e, recyclingHistoryTableColumns.DATE)} />
                                            </Collapse>
                                        </TableCell>
                                        <TableCell className={classes.head__filterCell}>
                                            <Collapse in={props.showFilters} timeout={200} className={classes.head__collapse}>
                                                <div className={classes.head__filterInputContainer}>
                                                    <Search size={12} className={classes.head__filterInputIcon} />
                                                    <input className={classes.head__filterInput} type="text" value={filterValues[recyclingHistoryTableColumns.BARCODE].value} onChange={(e) => handleSearch(e, recyclingHistoryTableColumns.BARCODE)} />
                                                </div>
                                            </Collapse>
                                        </TableCell>
                                        <TableCell className={classes.head__filterCell}>
                                            <Collapse in={props.showFilters} timeout={200} className={classes.head__collapse}>
                                                <div className={classes.head__filterInputContainer}>
                                                    <Search size={12} className={classes.head__filterInputIcon} />
                                                    <input className={classes.head__filterInput} type="text" value={filterValues[recyclingHistoryTableColumns.OPERATOR].value} onChange={(e) => handleSearch(e, recyclingHistoryTableColumns.OPERATOR)} />
                                                </div>
                                            </Collapse>
                                        </TableCell>
                                        <TableCell className={classes.head__filterCell}>
                                            <Collapse in={props.showFilters} timeout={200} className={classes.head__collapse}>
                                                {/* dropdown with all props.checkpoints */}
                                                <Select
                                                    value={filterValues[recyclingHistoryTableColumns.BOOKINGTYPE].value}
                                                    onChange={(e) => handleSearch(e, recyclingHistoryTableColumns.BOOKINGTYPE)}
                                                    displayEmpty
                                                    className={classes.head__filterSelect}
                                                    sx={{
                                                        '& .MuiSelect-select': {
                                                            padding: 0,
                                                        }
                                                    }}
                                                >
                                                    <MenuItem value="">{t("All")}</MenuItem>
                                                    {/* alphabetically map */}
                                                    {bookingTypes.sort((a, b) => t(a.name).localeCompare(t(b.name))).map((bookingType, index) => (
                                                        <MenuItem key={index} value={bookingType.name}>{t(bookingType.name)}</MenuItem>
                                                    ))}

                                                    {/* {bookingTypes.map((bookingType, index) => (
                                                        <MenuItem key={index} value={bookingType.name}>{t(bookingType.name)}</MenuItem>
                                                    ))} */}
                                                </Select>
                                            </Collapse>
                                        </TableCell>
                                        <TableCell className={classes.head__filterCell}>
                                            <Collapse in={props.showFilters} timeout={200} className={classes.head__collapse}>
                                                <Select
                                                    value={filterValues[recyclingHistoryTableColumns.CARRIERTYPE].value}
                                                    onChange={(e) => handleSearch(e, recyclingHistoryTableColumns.CARRIERTYPE)}
                                                    displayEmpty
                                                    className={classes.head__filterSelect}
                                                    sx={{
                                                        '& .MuiSelect-select': {
                                                            padding: 0,
                                                        }
                                                    }}
                                                >
                                                    <MenuItem value="">{t("All")}</MenuItem>
                                                    {scrapCarrierTypes.map((carrierType, index) => (
                                                        <MenuItem key={index} value={carrierType.name}>{t(carrierType.name)}</MenuItem>
                                                    ))}
                                                </Select>
                                            </Collapse>
                                        </TableCell>
                                        <TableCell className={classes.head__filterCell}>
                                            <Collapse in={props.showFilters} timeout={200} className={classes.head__collapse}>
                                                <Select
                                                    value={filterValues[recyclingHistoryTableColumns.MATERIALTYPE].value}
                                                    onChange={(e) => handleSearch(e, recyclingHistoryTableColumns.MATERIALTYPE)}
                                                    displayEmpty
                                                    className={classes.head__filterSelect}
                                                    sx={{
                                                        '& .MuiSelect-select': {
                                                            padding: 0,
                                                        }
                                                    }}
                                                >
                                                    <MenuItem value="">{t("All")}</MenuItem>
                                                    {scrapMaterialTypes.map((materialType, index) => (
                                                        <MenuItem key={index} value={materialType.name}>{t(materialType.name)}</MenuItem>
                                                    ))}
                                                </Select>
                                            </Collapse>
                                        </TableCell>
                                        <TableCell className={classes.head__filterCell}>
                                            <Collapse in={props.showFilters} timeout={200} className={classes.head__collapse}>
                                                <Select
                                                    value={filterValues[recyclingHistoryTableColumns.MATERIALCOLOR].value}
                                                    onChange={(e) => handleSearch(e, recyclingHistoryTableColumns.MATERIALCOLOR)}
                                                    displayEmpty
                                                    className={classes.head__filterSelect}
                                                    sx={{
                                                        '& .MuiSelect-select': {
                                                            padding: 0,
                                                        }
                                                    }}
                                                >
                                                    <MenuItem value="">{t("All")}</MenuItem>
                                                    {scrapColors.map((color, index) => (
                                                        <MenuItem key={index} value={color.colorName}>{t(color.colorName)}</MenuItem>
                                                    ))}
                                                </Select>
                                            </Collapse>
                                        </TableCell>
                                        <TableCell className={classes.head__filterCell}>
                                            <Collapse in={props.showFilters} timeout={200} className={classes.head__collapse}>
                                                <div className={classes.head__filterInputContainer}>
                                                    <Search size={12} className={classes.head__filterInputIcon} />
                                                    <input className={classes.head__filterInput} type="text" value={filterValues[recyclingHistoryTableColumns.NETWEIGHT].value} onChange={(e) => handleSearch(e, recyclingHistoryTableColumns.NETWEIGHT)} />
                                                </div>
                                            </Collapse>
                                        </TableCell>
                                        <TableCell className={classes.head__filterCell}>
                                            <Collapse in={props.showFilters} timeout={200} className={classes.head__collapse}>
                                                {/* dropdown with all recycling workstations */}
                                                <Select
                                                    value={filterValues[recyclingHistoryTableColumns.WASTELOCATION].value}
                                                    onChange={(e) => handleSearch(e, recyclingHistoryTableColumns.WASTELOCATION)}
                                                    displayEmpty
                                                    className={classes.head__filterSelect}
                                                    sx={{
                                                        '& .MuiSelect-select': {
                                                            padding: 0,
                                                        }
                                                    }}
                                                >
                                                    <MenuItem value="">All</MenuItem>
                                                    {sortWasteLocations(wasteLocations).map((wasteLocation, index) => (
                                                        <MenuItem key={index} value={wasteLocation.name}>{t(wasteLocation.name)}</MenuItem>
                                                    ))}
                                                </Select>
                                            </Collapse>
                                        </TableCell>
                                        <TableCell className={classes.head__filterCell}>
                                            <Collapse in={props.showFilters} timeout={200} className={classes.head__collapse}>
                                                {/* dropdown with all recycling workstations */}
                                                <Select
                                                    value={filterValues[recyclingHistoryTableColumns.WORKSTATION].value}
                                                    onChange={(e) => handleSearch(e, recyclingHistoryTableColumns.WORKSTATION)}
                                                    displayEmpty
                                                    className={classes.head__filterSelect}
                                                    sx={{
                                                        '& .MuiSelect-select': {
                                                            padding: 0,
                                                        }
                                                    }}
                                                >
                                                    <MenuItem value="">All</MenuItem>
                                                    {recyclingWorkstations.map((workstation, index) => (
                                                        <MenuItem key={index} value={workstation.name}>{workstation.name}</MenuItem>
                                                    ))}
                                                </Select>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>

                                    {/* Headers */}
                                    <TableRow className={classes.head__headerRow}>
                                        <TableCell className={classes.head__headCell}>
                                            <div className={classes.headCell__contentContainer}>
                                                {t("Date")}
                                                <div className={classes.groupingArrowsContainer}>
                                                    <div className={classes.groupingArrowUpContainer}>
                                                        <ChevronUp size={getGroupingIconSize(recyclingHistoryTableColumns.DATE, true)} className={classes.groupingArrowUp} style={{ color: getGroupingIconColor(recyclingHistoryTableColumns.DATE, true) }} onClick={() => handleGrouping(recyclingHistoryTableColumns.DATE, true)} />
                                                    </div>
                                                    <div className={classes.groupingArrowDownContainer}>
                                                        <ChevronDown size={getGroupingIconSize(recyclingHistoryTableColumns.DATE, false)} className={classes.groupingArrowDown} style={{ color: getGroupingIconColor(recyclingHistoryTableColumns.DATE, false) }} onClick={() => handleGrouping(recyclingHistoryTableColumns.DATE, false)} />
                                                    </div>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className={classes.head__headCell}>
                                            <div className={classes.headCell__contentContainer}>
                                                {t("Barcode")}
                                                <div className={classes.groupingArrowsContainer}>
                                                    <div className={classes.groupingArrowUpContainer}>
                                                        <ChevronUp size={getGroupingIconSize(recyclingHistoryTableColumns.BARCODE, true)} className={classes.groupingArrowUp} style={{ color: getGroupingIconColor(recyclingHistoryTableColumns.BARCODE, true) }} onClick={() => handleGrouping(recyclingHistoryTableColumns.BARCODE, true)} />
                                                    </div>
                                                    <div className={classes.groupingArrowDownContainer}>
                                                        <ChevronDown size={getGroupingIconSize(recyclingHistoryTableColumns.BARCODE, false)} className={classes.groupingArrowDown} style={{ color: getGroupingIconColor(recyclingHistoryTableColumns.BARCODE, false) }} onClick={() => handleGrouping(recyclingHistoryTableColumns.BARCODE, false)} />
                                                    </div>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className={classes.head__headCell}>
                                            <div className={classes.headCell__contentContainer}>
                                                {t("Operator")}
                                                <div className={classes.groupingArrowsContainer}>
                                                    <div className={classes.groupingArrowUpContainer}>
                                                        <ChevronUp size={getGroupingIconSize(recyclingHistoryTableColumns.OPERATOR, true)} className={classes.groupingArrowUp} style={{ color: getGroupingIconColor(recyclingHistoryTableColumns.OPERATOR, true) }} onClick={() => handleGrouping(recyclingHistoryTableColumns.OPERATOR, true)} />
                                                    </div>
                                                    <div className={classes.groupingArrowDownContainer}>
                                                        <ChevronDown size={getGroupingIconSize(recyclingHistoryTableColumns.OPERATOR, false)} className={classes.groupingArrowDown} style={{ color: getGroupingIconColor(recyclingHistoryTableColumns.OPERATOR, false) }} onClick={() => handleGrouping(recyclingHistoryTableColumns.OPERATOR, false)} />
                                                    </div>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className={classes.head__headCell}>
                                            <div className={classes.headCell__contentContainer} style={{ justifyContent: "center" }}>
                                                {t("Booking type")}
                                                <div className={classes.groupingArrowsContainer}>
                                                    <div className={classes.groupingArrowUpContainer}>
                                                        <ChevronUp size={getGroupingIconSize(recyclingHistoryTableColumns.BOOKINGTYPE, true)} className={classes.groupingArrowUp} style={{ color: getGroupingIconColor(recyclingHistoryTableColumns.BOOKINGTYPE, true) }} onClick={() => handleGrouping(recyclingHistoryTableColumns.BOOKINGTYPE, true)} />
                                                    </div>
                                                    <div className={classes.groupingArrowDownContainer}>
                                                        <ChevronDown size={getGroupingIconSize(recyclingHistoryTableColumns.BOOKINGTYPE, false)} className={classes.groupingArrowDown} style={{ color: getGroupingIconColor(recyclingHistoryTableColumns.BOOKINGTYPE, false) }} onClick={() => handleGrouping(recyclingHistoryTableColumns.BOOKINGTYPE, false)} />
                                                    </div>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className={classes.head__headCell}>
                                            <div className={classes.headCell__contentContainer}>
                                                {t("Carrier type")}
                                                <div className={classes.groupingArrowsContainer}>
                                                    <div className={classes.groupingArrowUpContainer}>
                                                        <ChevronUp size={getGroupingIconSize(recyclingHistoryTableColumns.CARRIERTYPE, true)} className={classes.groupingArrowUp} style={{ color: getGroupingIconColor(recyclingHistoryTableColumns.CARRIERTYPE, true) }} onClick={() => handleGrouping(recyclingHistoryTableColumns.CARRIERTYPE, true)} />
                                                    </div>
                                                    <div className={classes.groupingArrowDownContainer}>
                                                        <ChevronDown size={getGroupingIconSize(recyclingHistoryTableColumns.CARRIERTYPE, false)} className={classes.groupingArrowDown} style={{ color: getGroupingIconColor(recyclingHistoryTableColumns.CARRIERTYPE, false) }} onClick={() => handleGrouping(recyclingHistoryTableColumns.CARRIERTYPE, false)} />
                                                    </div>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className={classes.head__headCell}>
                                            <div className={classes.headCell__contentContainer}>
                                                {t("Material type")}
                                                <div className={classes.groupingArrowsContainer}>
                                                    <div className={classes.groupingArrowUpContainer}>
                                                        <ChevronUp size={getGroupingIconSize(recyclingHistoryTableColumns.MATERIALTYPE, true)} className={classes.groupingArrowUp} style={{ color: getGroupingIconColor(recyclingHistoryTableColumns.MATERIALTYPE, true) }} onClick={() => handleGrouping(recyclingHistoryTableColumns.MATERIALTYPE, true)} />
                                                    </div>
                                                    <div className={classes.groupingArrowDownContainer}>
                                                        <ChevronDown size={getGroupingIconSize(recyclingHistoryTableColumns.MATERIALTYPE, false)} className={classes.groupingArrowDown} style={{ color: getGroupingIconColor(recyclingHistoryTableColumns.MATERIALTYPE, false) }} onClick={() => handleGrouping(recyclingHistoryTableColumns.MATERIALTYPE, false)} />
                                                    </div>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className={classes.head__headCell}>
                                            <div className={classes.headCell__contentContainer}>
                                                {t("Material color")}
                                                <div className={classes.groupingArrowsContainer}>
                                                    <div className={classes.groupingArrowUpContainer}>
                                                        <ChevronUp size={getGroupingIconSize(recyclingHistoryTableColumns.MATERIALCOLOR, true)} className={classes.groupingArrowUp} style={{ color: getGroupingIconColor(recyclingHistoryTableColumns.MATERIALCOLOR, true) }} onClick={() => handleGrouping(recyclingHistoryTableColumns.MATERIALCOLOR, true)} />
                                                    </div>
                                                    <div className={classes.groupingArrowDownContainer}>
                                                        <ChevronDown size={getGroupingIconSize(recyclingHistoryTableColumns.MATERIALCOLOR, false)} className={classes.groupingArrowDown} style={{ color: getGroupingIconColor(recyclingHistoryTableColumns.MATERIALCOLOR, false) }} onClick={() => handleGrouping(recyclingHistoryTableColumns.MATERIALCOLOR, false)} />
                                                    </div>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className={classes.head__headCell} style={{ textAlign: "right" }}>
                                            <div className={classes.headCell__contentContainer} style={{ justifyContent: "flex-end" }}>
                                                {t("Net weight")}
                                                <div className={classes.groupingArrowsContainer}>
                                                    <div className={classes.groupingArrowUpContainer}>
                                                        <ChevronUp size={getGroupingIconSize(recyclingHistoryTableColumns.NETWEIGHT, true)} className={classes.groupingArrowUp} style={{ color: getGroupingIconColor(recyclingHistoryTableColumns.NETWEIGHT, true) }} onClick={() => handleGrouping(recyclingHistoryTableColumns.NETWEIGHT, true)} />
                                                    </div>
                                                    <div className={classes.groupingArrowDownContainer}>
                                                        <ChevronDown size={getGroupingIconSize(recyclingHistoryTableColumns.NETWEIGHT, false)} className={classes.groupingArrowDown} style={{ color: getGroupingIconColor(recyclingHistoryTableColumns.NETWEIGHT, false) }} onClick={() => handleGrouping(recyclingHistoryTableColumns.NETWEIGHT, false)} />
                                                    </div>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className={classes.head__headCell}>
                                            <div className={classes.headCell__contentContainer}>
                                                {t("Waste location")}
                                                <div className={classes.groupingArrowsContainer}>
                                                    <div className={classes.groupingArrowUpContainer}>
                                                        <ChevronUp size={getGroupingIconSize(recyclingHistoryTableColumns.WASTELOCATION, true)} className={classes.groupingArrowUp} style={{ color: getGroupingIconColor(recyclingHistoryTableColumns.WASTELOCATION, true) }} onClick={() => handleGrouping(recyclingHistoryTableColumns.WASTELOCATION, true)} />
                                                    </div>
                                                    <div className={classes.groupingArrowDownContainer}>
                                                        <ChevronDown size={getGroupingIconSize(recyclingHistoryTableColumns.WASTELOCATION, false)} className={classes.groupingArrowDown} style={{ color: getGroupingIconColor(recyclingHistoryTableColumns.WASTELOCATION, false) }} onClick={() => handleGrouping(recyclingHistoryTableColumns.WASTELOCATION, false)} />
                                                    </div>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className={classes.head__headCell}>
                                            <div className={classes.headCell__contentContainer}>
                                                {t("Workstation")}
                                                <div className={classes.groupingArrowsContainer}>
                                                    <div className={classes.groupingArrowUpContainer}>
                                                        <ChevronUp size={getGroupingIconSize(recyclingHistoryTableColumns.WORKSTATION, true)} className={classes.groupingArrowUp} style={{ color: getGroupingIconColor(recyclingHistoryTableColumns.WORKSTATION, true) }} onClick={() => handleGrouping(recyclingHistoryTableColumns.WORKSTATION, true)} />
                                                    </div>
                                                    <div className={classes.groupingArrowDownContainer}>
                                                        <ChevronDown size={getGroupingIconSize(recyclingHistoryTableColumns.WORKSTATION, false)} className={classes.groupingArrowDown} style={{ color: getGroupingIconColor(recyclingHistoryTableColumns.WORKSTATION, false) }} onClick={() => handleGrouping(recyclingHistoryTableColumns.WORKSTATION, false)} />
                                                    </div>
                                                </div>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                {props.recyclingHistory.length > 0 ?
                                    <TableBody className={classes.body}>
                                        {filteredRecyclingHistory.filter(x => x.visible).slice(pg * rpg, pg * rpg + rpg).map((row, index) => (
                                            <TableRow key={index} className={classes.body__row}>
                                                <TableCell className={classes.body__cell}>{formatDate(row.date)}</TableCell>
                                                <TableCell className={classes.body__cell}>{row.barcode}</TableCell>
                                                <TableCell className={classes.body__cell}>{row.operator}</TableCell>
                                                <TableCell className={classes.body__cell}>{formatBookingType(row.bookingType)}</TableCell>
                                                <TableCell className={classes.body__cell}>{t(row.carrierType)}</TableCell>
                                                <TableCell className={classes.body__cell}>{t(row.materialType)}</TableCell>
                                                <TableCell className={classes.body__cell}>{formatColor(row.materialColor, row.materialColorHex)}</TableCell>
                                                <TableCell className={classes.body__cell} style={{ textAlign: "right" }}>{formatWeight(row.netWeight)}</TableCell>
                                                <TableCell className={classes.body__cell}>{t(row.wasteLocation)}</TableCell>
                                                <TableCell className={classes.body__cell}>{row.workstation}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                    : null}
                            </Table>
                        </TableContainer>
                        {props.recyclingHistory.length == 0 ?
                            null
                            :
                            <TablePagination
                                rowsPerPageOptions={[25, 50, 100]}
                                labelRowsPerPage={t("Rijen per pagina") + ":"}
                                labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t("van")} ${count}`}
                                component="div"
                                count={filteredRecyclingHistory.filter(x => x.visible).length}
                                rowsPerPage={rpg}
                                page={pg}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        }
                    </React.Fragment>
                    {filteredRecyclingHistory.filter(x => x.visible).length > 0 && !props.loading ? null :
                        <div className={classes.emptyStateContainer}>
                            <p className={classes.emptyState__text}>
                                {t("No Recycling history found")}
                            </p>
                        </div>
                    }
                </div>
            }
        </React.Fragment >
    )


}

const useStyles = makeStyles({

    paper: {
        boxShadow: 'none',
        backgroundColor: 'transparent',
    },

    title: {
        fontSize: "22px",
        fontFamily: "Lato, sans-serif !important",
        fontWeight: 'medium',
        color: "#020100",
        float: "left",
        marginBottom: "10px",
        paddingLeft: "20px",
        marginTop: "15px"
    },

    head: {

    },

    head__filterRow: {
        height: 'fit-content',
    },

    collapse: {
        float: "left",
        width: "100%",
    },

    head__filterCell: {
        padding: "0px 24px 8px 24px !important",


        "&:first-child": {
            paddingLeft: "14px !important",
        },

        "&:last-child": {
            paddingRight: "14px !important",
        },
    },

    head__filterInputContainer: {
        position: 'relative',
    },

    head__filterInputIcon: {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        left: '8px',
    },

    head__filterInput: {
        width: '100%',
        height: '28px',
        borderRadius: '3px',
        border: '1px solid #A9A9A9',

        padding: '0px 8px',
        paddingLeft: '24px',
        boxSizing: 'border-box',

        fontSize: '16px',
        fontFamily: 'Lato, sans-serif !important',
        fontWeight: 'regular',
        color: '#0C0E29',

        "&:focus": {
            outline: "none",
            border: "1px solid #4654FB",
        },
    },

    head__filterSelect: {
        width: '100%',
        height: '28px',
        borderRadius: '3px !important',
        border: '1px solid #A9A9A9 !important',

        padding: '0px 8px',
        boxSizing: 'border-box !important',

        fontSize: '16px !important',
        fontFamily: 'Lato, sans-serif !important',
        fontWeight: 'normal !important',
        color: '#0C0E29 !important',

        backgroundColor: 'white',

        "&:focus": {
            outline: "none",
            border: "1px solid #4654FB",
        },
    },

    head__filterInput_date: {
        width: '100%',
        height: '28px',
        borderRadius: '3px',
        border: '1px solid #A9A9A9',

        padding: '0px 8px',
        boxSizing: 'border-box',

        fontSize: '16px',
        fontFamily: 'Lato, sans-serif !important',
        fontWeight: 'regular',
        color: '#0C0E29',

        "&:focus": {
            outline: "none",
            border: "1px solid #4654FB",
        },
    },

    head__headerRow: {
        backgroundColor: '#E2E4F5',
        borderRadius: "6px",
    },

    head__headCell: {
        fontSize: "16px !important",
        fontFamily: "Lato, sans-serif !important",
        fontWeight: "bold !important",
        color: "#0C0E29",

        padding: "8px 24px !important",
        paddingTop: "0px",
        border: "none",

        whiteSpace: "nowrap",

        "&:first-child": {
            borderRadius: "6px 0 0 6px",
            paddingLeft: "14px !important",
        },

        "&:last-child": {
            borderRadius: "0 6px 6px 0",
            paddingRight: "14px !important",
        },
    },

    headCell__contentContainer: {
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        gap: "8px"
    },

    body: {

    },

    body__row: {
        "&:nth-of-type(even)": {
            backgroundColor: "#F7F7F7",
            mixBlendMode: "multiply",
        },
    },

    body__cell: {
        fontSize: "16px !important",
        fontFamily: "Lato, sans-serif !important",
        fontWeight: "medium",
        color: "#0C0E29",

        padding: "8px 24px !important",
        paddingTop: "0px",
        border: "none",

        whiteSpace: "nowrap",

        "&:first-child": {
            borderRadius: "6px 0 0 6px",
            paddingLeft: "14px !important",
        },

        "&:last-child": {
            borderRadius: "0 6px 6px 0",
            paddingRight: "14px !important",
        },
    },

    emptyStateContainer: {
        width: '100%',

        textAlign: "center",
        marginTop: "192px",
    },

    emptyState__text: {
        fontSize: "24px",
        fontFamily: "Lato, sans-serif !important",
        fontWeight: "medium",
        color: "#0C0E29",
    },

    tableContainer: {
        height: "auto"
    },

    row: {
        '&:nth-of-type(odd)': {
            backgroundColor: "#F4F4F4",
        },
    },

    table: {
        borderSpacing: '0px 5px',
        borderCollapse: 'separate',
    },

    groupingArrowsContainer: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: "0px",
        width: "fit-content"
    },

    groupingArrowUpContainer: {
        position: "relative",
        width: "16px",
        height: "12px",

        "&:hover": {
            cursor: "pointer",

            "& $groupingArrowUp": {
                width: "16px",
                height: "16px",

                transition: "all .1s ease-in",

                color: "#4654FB"
            },
        }
    },

    groupingArrowDownContainer: {
        position: "relative",
        width: "16px",
        height: "12px",

        overflow: "hidden",

        "&:hover": {
            cursor: "pointer",

            "& $groupingArrowDown": {
                width: "16px",
                height: "16px",

                transition: "all .1s ease-in",

                color: "#4654FB"
            },
        }
    },

    groupingArrowUp: {
        position: "absolute",
        bottom: "0%",
        left: "50%",
        transform: "translate(-50%, 0%)",
    },

    groupingArrowDown: {
        position: "absolute",
        top: "0%",
        left: "50%",
        transform: "translate(-50%, 0%)",
    },

    loadingBalls: {
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
    },

    "@global": {
        "@keyframes rotation": {
            "0%": { transform: "rotate(0deg)" },
            "100%": { transform: "rotate(360deg)" },
        },
    },

    loaderContainer: {
        height: "60vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },

    loader: {
        width: "48px",
        height: "48px",
        border: "5px solid " + FlagstoneTheme.palette.primary.main,
        borderBottomColor: "transparent",
        borderRadius: "50%",
        display: "inline-block",
        boxSizing: "border-box",
        animation: "rotation 1s linear infinite",
    },

    colorContainer: {
        display: "flex",
        alignItems: "center",
        gap: "8px"
    },

    colorSwatch: {
        width: "20px",
        height: "20px",
        borderRadius: "50%",
        boxSizing: "border-box",
    },

    colorName: {
        fontSize: "16px",
        fontFamily: "Lato, sans-serif",
        fontWeight: "medium",
        color: "#0C0E29",
        margin: "0",
    },

});

function mapStateToProps(state) {
    return {
        workstation: state.workstation,
        general: state.general,
        user: state.user
    }
}

export default connect(mapStateToProps)(RecyclingHistoryOverviewTable)